import { useState } from "react";
import type { ComponentType, CSSProperties } from "react";
import Icon from "../Icon/Icon.jsx";
import {
  ResponsiveIFrame as ResponsiveIFrameUntyped,
  responsiveIFrameContainer,
} from "./ResponsiveIFrame.jsx";
import styles from "./VideoGate.css";

// `ResponsiveIFrame.jsx` is untyped JS, so give its exports the precise shapes
// we rely on.
const ResponsiveIFrame = ResponsiveIFrameUntyped as ComponentType<{
  ratio?: number;
  src: string;
  allow?: string;
  referrerPolicy?: React.HTMLAttributeReferrerPolicy;
  title?: string;
}>;

type VideoService = "youtube" | "vimeo" | "arte" | "ardmediathek" | "zdf";

const providerLabels: Record<VideoService, string> = {
  youtube: "YouTube",
  vimeo: "Vimeo",
  arte: "arte",
  ardmediathek: "ARD Mediathek",
  zdf: "ZDF",
};

type VideoGateProps = {
  service: VideoService;
  id: string;
  /** The provider embed URL to load into the iframe on click. */
  embedSrc: string;
  /** `allow` attribute for the iframe (autoplay/fullscreen/…). */
  allow?: string;
  ratio: number;
  /**
   * `true` when `ratio` is only our 16:9 default rather than something the
   * content stated. The poster's own dimensions then override it — see
   * `measuredRatio` below.
   */
  ratioIsFallback?: boolean;
  referrerPolicy?: React.HTMLAttributeReferrerPolicy;
  /** arte only — language segment for the poster lookup. */
  lang?: string;
  /** vimeo only — privacy hash of an unlisted video, needed to resolve its poster. */
  hash?: string;
  /** Provider-specific autoplay query param, appended to `embedSrc` on load. */
  autoplayParam?: string;
  title?: string;
};

/**
 * Click-to-play gate for external video embeds. Until the user clicks, only a
 * poster image (served from our own `/video-poster/img` endpoint) is rendered —
 * the third-party iframe (and therefore any request to Google/Vimeo/ARD/ZDF/arte)
 * is not mounted, satisfying the GDPR "no third-party request before consent"
 * requirement. Consent is per-click and never persisted.
 */
const VideoGate = ({
  service,
  id,
  embedSrc,
  allow,
  ratio,
  ratioIsFallback,
  referrerPolicy,
  lang,
  hash,
  autoplayParam,
  title,
}: VideoGateProps) => {
  const [loaded, setLoaded] = useState(false);
  const [posterFailed, setPosterFailed] = useState(false);
  const [measuredRatio, setMeasuredRatio] = useState<number | null>(null);

  // Nothing in the CMS records a video's orientation today, so portrait embeds
  // (YouTube Shorts and friends) would all render in a 16:9 box. The poster we
  // serve carries the video's real shape, so we measure it once it decodes and
  // adopt that — no extra request, at the cost of a one-off layout shift.
  // Deliberately kept as a *fallback* only: content that states its ratio wins.
  const effectiveRatio =
    ratioIsFallback && measuredRatio !== null ? measuredRatio : ratio;

  if (loaded) {
    // Single click loads *and* plays — append the provider autoplay param.
    const src = autoplayParam
      ? `${embedSrc}${embedSrc.includes("?") ? "&" : "?"}${autoplayParam}`
      : embedSrc;

    return (
      <ResponsiveIFrame
        ratio={effectiveRatio}
        src={src}
        allow={allow}
        referrerPolicy={referrerPolicy}
        title={title}
      />
    );
  }

  const label = providerLabels[service];

  // Poster is served from our own (auth-protected) origin, so the third-party
  // provider is never contacted from the browser. `crossOrigin="use-credentials"`
  // forwards the session cookie (the endpoint is behind `withAuth`), and
  // `loading="lazy"` defers the request until the embed nears the viewport.
  const posterUrl = `${import.meta.env.VITE_API_HOST}/video-poster/img?service=${service}&id=${encodeURIComponent(
    id,
  )}${lang ? `&lang=${lang}` : ""}${hash ? `&hash=${hash}` : ""}`;

  return (
    <div style={responsiveIFrameContainer(effectiveRatio) as CSSProperties}>
      <button
        type="button"
        className={styles.gate}
        onClick={() => setLoaded(true)}
        aria-label={`${label}-Video laden und abspielen`}
      >
        {posterFailed ? (
          <span className={styles.fallback}>
            <Icon name="video_library" size="48px" className={undefined} />
            <span className={styles.fallbackLabel}>{label}</span>
          </span>
        ) : (
          <img
            className={styles.poster}
            src={posterUrl}
            crossOrigin="use-credentials"
            loading="lazy"
            decoding="async"
            alt=""
            onLoad={(e) => {
              const { naturalWidth, naturalHeight } = e.currentTarget;
              if (naturalWidth > 0 && naturalHeight > 0) {
                setMeasuredRatio(naturalHeight / naturalWidth);
              }
            }}
            onError={() => setPosterFailed(true)}
          />
        )}
        <span className={styles.playButton}>
          <svg
            className={styles.playIcon}
            width="24"
            height="24"
            viewBox="0 0 24 24"
            aria-hidden
          >
            <path d="M8 5v14l11-7z" />
          </svg>
        </span>
        <span className={styles.providerLabel}>{label}</span>
      </button>
    </div>
  );
};

export default VideoGate;
